/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Import Workspace
 *
 * File:
 * src/modules/campaign/prospectImport/components/importWorkspace/upload/UploadPanel.jsx
 *
 * Description:
 * Upload Stage of the ERP Import Framework.
 *
 * Responsibilities
 * ----------------
 * • Import Header
 * • Instructions
 * • Download Template
 * • Drag & Drop Upload
 * • Browse File
 * • Selected File Information
 * • Verify File
 *
 * This component contains no business logic.
 * All actions are delegated through props.
 * ============================================================================
 */

import React from "react";

import {
    Card,
    Row,
    Col,
    Button,
    Alert,
    Form
} from "react-bootstrap";

//import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

//import {
//    faDownload,
//    faFileExcel,
//    faCloudUploadAlt,
//    faCheckCircle
//} from "@fortawesome/free-solid-svg-icons";

export default function UploadPanel({

    config,

    state,

    actions,

    readOnly = false

}) {

    // ==========================================================
    // CONFIG
    // ==========================================================

    const {

        module,

        feature,

        templateName,

        templateVersion,

        supportedExtensions,

        maxFileSizeMB,

        instructions

    } = config;

    // ==========================================================
    // STATE
    // ==========================================================

    const {

        selectedFile,

        verified,

        loading,

        message,

        dragActive

    } = state;

    // ==========================================================
    // ACTIONS
    // ==========================================================

    const {

        downloadTemplate,

        handleFileChange,

        verifyFile,

        handleDrop,

        handleDragEnter,

        handleDragLeave,

        handleDragOver

    } = actions;

    // ==========================================================
    // FILE SIZE
    // ==========================================================

    function formatFileSize(bytes = 0) {

        if (bytes < 1024)
            return `${bytes} Bytes`;

        if (bytes < 1024 * 1024)
            return `${(bytes / 1024).toFixed(2)} KB`;

        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

    }

    // ==========================================================
    // RENDER
    // ==========================================================

    return (

        <Card className="shadow-sm">

            {/* ====================================================== */}
            {/* HEADER */}
            {/* ====================================================== */}

            <Card.Header>

                <Row className="align-items-center">

                    <Col>

                        <h4 className="mb-1">

                            {feature}

                        </h4>

                        <small className="text-muted">

                            {module}

                        </small>

                    </Col>

                    <Col xs="auto">

                        <Button

                            variant="outline-primary"

                            onClick={downloadTemplate}

                        >

                            <i className="fas fa-download me-2"></i>

                            Download Template

                        </Button>

                    </Col>

                </Row>

            </Card.Header>

            {/* ====================================================== */}
            {/* BODY */}
            {/* ====================================================== */}

            <Card.Body>

                {/* ================================================== */}
                {/* INSTRUCTION */}
                {/* ================================================== */}

                <Alert variant="info">

                    {instructions}

                </Alert>

                <Row>

                    {/* ================================================== */}
                    {/* DROP ZONE */}
                    {/* ================================================== */}

                    <Col lg={7}>
                                            <div

                            className={
                                `border rounded p-5 text-center ${
                                    dragActive
                                        ? "border-primary bg-light"
                                        : "border-secondary"
                                }`
                            }

                            onDrop={handleDrop}

                            onDragEnter={handleDragEnter}

                            onDragLeave={handleDragLeave}

                            onDragOver={handleDragOver}

                        >

                            <i
                            className="fas fa-cloud-upload-alt text-primary mb-4"
                                style={{ fontSize: "4rem" }}
                            ></i>

                            <h5>

                                Drag & Drop Excel File Here

                            </h5>

                            <p className="text-muted">

                                or

                            </p>

                            <Form.Control

                                id="prospect-import-file"

                                type="file"

                                accept={supportedExtensions
                                    .map(ext => `.${ext}`)
                                    .join(",")}

                                className="d-none"

                                disabled={readOnly}

                                onChange={handleFileChange}

                            />

                            <Button

                                variant="primary"

                                disabled={readOnly}

                                onClick={() =>

                                    document
                                        .getElementById(
                                            "prospect-import-file"
                                        )
                                        ?.click()

                                }

                            >

                                <i className="fas fa-file-excel me-2"></i>

                                Choose File

                            </Button>

                            <hr />

                            <Row>

                                <Col md={6}>

                                    <small className="text-muted">

                                        <strong>

                                            Supported Files

                                        </strong>

                                        <br />

                                        {supportedExtensions.join(", ")}

                                    </small>

                                </Col>

                                <Col md={6}>

                                    <small className="text-muted">

                                        <strong>

                                            Maximum Upload Size

                                        </strong>

                                        <br />

                                        {maxFileSizeMB} MB

                                    </small>

                                </Col>

                            </Row>

                        </div>

                    </Col>

                    {/* ================================================== */}
                    {/* IMPORT INFORMATION */}
                    {/* ================================================== */}

                    <Col lg={5}>

                        <Card
                            bg="light"
                            className="h-100"
                        >

                            <Card.Header>

                                <strong>

                                    Import Information

                                </strong>

                            </Card.Header>

                            <Card.Body>

                                <table className="table table-sm mb-0">

                                    <tbody>

                                        <tr>

                                            <th
                                                width="40%"
                                            >

                                                Module

                                            </th>

                                            <td>

                                                {module}

                                            </td>

                                        </tr>

                                        <tr>

                                            <th>

                                                Feature

                                            </th>

                                            <td>

                                                {feature}

                                            </td>

                                        </tr>

                                        <tr>

                                            <th>

                                                Template

                                            </th>

                                            <td>

                                                {templateName}

                                            </td>

                                        </tr>

                                        <tr>

                                            <th>

                                                Version

                                            </th>

                                            <td>

                                                {templateVersion}

                                            </td>

                                        </tr>

                                        <tr>

                                            <th>

                                                File Types

                                            </th>

                                            <td>

                                                {supportedExtensions.join(", ")}

                                            </td>

                                        </tr>

                                        <tr>

                                            <th>

                                                Max Size

                                            </th>

                                            <td>

                                                {maxFileSizeMB} MB

                                            </td>

                                        </tr>

                                    </tbody>

                                </table>

                            </Card.Body>

                        </Card>

                    </Col>

                </Row>
                                {/* ================================================== */}
                {/* SELECTED FILE */}
                {/* ================================================== */}

                {selectedFile && (

                    <Card className="mt-4 border-success">

                        <Card.Header>

                            <Row className="align-items-center">

                                <Col>

                                    <strong>

                                        Selected File

                                    </strong>

                                </Col>

                                <Col
                                    xs="auto"
                                >

                                    {verified ? (

                                        <span className="badge bg-success">

                                            Verified

                                        </span>

                                    ) : (

                                        <span className="badge bg-warning text-dark">

                                            Ready for Verification

                                        </span>

                                    )}

                                </Col>

                            </Row>

                        </Card.Header>

                        <Card.Body>

                            <Row className="align-items-center">

                                <Col lg={8}>

                                    <h6 className="mb-3">

                                        <i className="fas fa-file-excel text-success me-2"></i>

                                        {selectedFile.name}

                                    </h6>

                                    <Row>

                                        <Col md={4}>

                                            <small className="text-muted">

                                                File Size

                                            </small>

                                            <div>

                                                {formatFileSize(
                                                    selectedFile.size
                                                )}

                                            </div>

                                        </Col>

                                        <Col md={4}>

                                            <small className="text-muted">

                                                Status

                                            </small>

                                            <div>

                                                {verified
                                                    ? "Verified"
                                                    : "Ready"}

                                            </div>

                                        </Col>

                                        <Col md={4}>

                                            <small className="text-muted">

                                                Last Modified

                                            </small>

                                            <div>

                                                {new Date(
                                                    selectedFile.lastModified
                                                ).toLocaleString()}

                                            </div>

                                        </Col>

                                    </Row>

                                </Col>

                                <Col
                                    lg={4}
                                    className="text-lg-end mt-3 mt-lg-0"
                                >

                                    <Button

                                        variant="success"

                                        size="lg"

                                        disabled={
                                            loading ||
                                            verified
                                        }

                                        onClick={verifyFile}

                                    >

                                        <i className="fas fa-check-circle me-2"></i>

                                        {

                                            loading

                                                ? "Verifying..."

                                                : verified

                                                    ? "Verified"

                                                    : "Verify File"

                                        }

                                    </Button>

                                </Col>

                            </Row>

                        </Card.Body>

                    </Card>

                )}

                {/* ================================================== */}
                {/* MESSAGE */}
                {/* ================================================== */}

                {message && (

                    <Alert

                        className="mt-4 mb-0"

                        variant={
                            verified
                                ? "success"
                                : "warning"
                        }

                    >

                        {message}

                    </Alert>

                )}

            </Card.Body>

        </Card>

    );

}